import React, {useContext, useEffect, useState} from "react"
import {ChartScopeSettings} from "./insight/chart-scope-settings/chart-scope-settings"
import {Category} from "../../domain/category"
import useSpendingData from "./insight/use-spending-data"
import {CategoryHelper, CategoryHelperContext} from "../../services/helpers/category-helper"
import {CategorySettings, DefaultExcludeCategoryIds} from "./insight/category-settings/category-settings"
import {D3SpendingBarChart} from "./category-chart/co2-or-spending-pie-chart"
import {ChartScopeTransactions} from "./insight/chart-scope-transactions"
import styles from "./dashboard.module.scss"
import MaterialIcon from "../../components-shared/material-icon/material-icon-view"
import {ImportModal} from "./import/import-modal"
import {Message} from "../../components-shared/message/message"
import {ImportTransactionsSuccessResponse} from "../../services/api/transactions-api"
import {Panel} from "../../components-shared/panel/panel"
import {Heading} from "../../components-shared/heading/heading"
import {Income} from "./income/income"
import {DashboardProgressChartArea} from "./progress-chart/dashboard-progress-chart-area"
import {ModeToggleButton} from "../../components-shared/mode-toggle-button/mode-toggle-button"
import marginStyles from "../../styles/shared-modules/margins.module.scss"
import {gaSendDashboardEvent, gaSendImportEvent} from "../../services/analytics/google-analytics-helper"

type ViewMode = "expenses" | "co2" | "income"

export const DashboardPage: React.FC = () => {
  const [showChartSettings, setShowChartSettings] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<ViewMode>("expenses")
  const [chartScopeSettings, setChartScopeSettings] = useState<ChartScopeSettings>({ range: "lastYear", period: "month" })
  const [transactionsChartScopeSettings, setTransactionsChartScopeSettings] = useState<ChartScopeSettings>({ range: "lastYear", period: "month" })
  const [applicableCategories, setApplicableCategories] = useState<Category[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
  const [showTransactions, setShowTransactions] = useState<boolean>(true)
  const [transactionsCategoryId, setTransactionsCategoryId] = useState<number | undefined>(undefined)
  const [spendingData, allSpendingData, transactionRange, refreshSpendingData] = useSpendingData(chartScopeSettings.range, applicableCategories)
  const categoryHelper = useContext<CategoryHelper | null>(CategoryHelperContext)
  const [excludeCategoryIds, setExcludeCategoryIds] = useState(DefaultExcludeCategoryIds) // TODO: replace with data from backend
  const [showImportModal, setShowImportModal] = useState<boolean>(false)
  const [message, setMessage] = useState<string | null>(null)

  const showExpensesOrImpact = viewMode === "expenses" || viewMode === "co2"
  const showCo2 = viewMode === "co2"
  const showIncome = viewMode === "income"

  useEffect(() => {
    if (categoryHelper != null) {
      setApplicableCategories(categoryHelper.categories
        .filter(x => excludeCategoryIds.indexOf(x.id) === -1))
    }
  }, [categoryHelper, excludeCategoryIds])

  useEffect(() => {
    setTransactionsChartScopeSettings({...chartScopeSettings})
  }, [chartScopeSettings])

  function toggleExcludeCategory(categoryId: number) {
    const index = excludeCategoryIds.indexOf(categoryId)

    if (index === -1) setExcludeCategoryIds(excludeCategoryIds.concat([categoryId]))
    else setExcludeCategoryIds(excludeCategoryIds.filter(x => x !== categoryId))
  }

  const refreshTransactionData = () => {
    refreshSpendingData()
    // TODO: set last transactions changed date and pass it to Transactions so transactions will also reload
  }

  const hasTransactions = spendingData !== null && spendingData.monthlySpendingData.months.length > 0

  const onImportSuccess = (importTransactionsSuccessResponse: ImportTransactionsSuccessResponse) => {
    gaSendImportEvent()
    refreshTransactionData()
    setMessage(`Importeren geslaagd: ${importTransactionsSuccessResponse.amount} transacties geimporteerd`)

    setTimeout(() => {
      setMessage(null)
    }, 5000)
  }

  const clickCategory = (category: Category) => {
    gaSendDashboardEvent("Click spending chart category")
    if (category.parentId === undefined) {
      setSelectedCategoryId(category.id)
    }
    setTransactionsCategoryId(category.id)
    setShowTransactions(true)

    if (spendingData !== null) {
      setTransactionsChartScopeSettings({...chartScopeSettings})
    }
  }

  const clickRemoveCategory = () => {
    gaSendDashboardEvent("Deselect category")
    if (selectedCategoryId !== null) {
      setSelectedCategoryId(null)
      setTransactionsCategoryId(undefined)
    }
  }

  const resetCategoryScope = (selectedCategoryId !== null)
    ? { text: "alles tonen", resetCategoryScope: clickRemoveCategory }
    : undefined

  const titleMetric = showCo2 ? "Impact" : "Uitgaven"
  const hasNoData = allSpendingData !== null && allSpendingData.monthlySpendingData.months.length === 0

  return <>
    <div className={styles.Settings}>
      <button className={showChartSettings ? styles.Selected : ""} onClick={() => setShowChartSettings(!showChartSettings)}><MaterialIcon name="tune" size="large"/></button>
      <button onClick={() => setShowImportModal(true)} className={styles.UploadButton}><MaterialIcon name="file_upload" size="large"/> <span>importeren</span></button>
    </div>

    <div className={styles.ViewToggle}>
      <ModeToggleButton isEnabled={viewMode === "expenses"} onClick={() => setViewMode("expenses")}>Uitgaven</ModeToggleButton>
      <ModeToggleButton isEnabled={viewMode === "co2"} onClick={() => setViewMode("co2")}>CO<sub>2</sub></ModeToggleButton>
      <ModeToggleButton isEnabled={viewMode === "income"} onClick={() => setViewMode("income")}>Inkomen</ModeToggleButton>
    </div>

    {showChartSettings && <Panel className={marginStyles.DefaultPanelMargins} fullWidth>
      <div className={marginStyles.DefaultSideMargins}>
        <Heading size="small">Categorie&euml;n weergeven</Heading>
        <CategorySettings excludeCategoryIds={excludeCategoryIds} toggleExcludeCategory={toggleExcludeCategory}/>
      </div>
    </Panel>}

    {!hasNoData && <>
      {allSpendingData && showIncome &&
        <Income
          className={marginStyles.DefaultPanelMargins}
          type="bar"
          spendingData={allSpendingData}
          chartScopeSettings={chartScopeSettings}
          refreshSpendingData={refreshTransactionData}
          transactionRange={transactionRange}/>}

      {spendingData && allSpendingData && showExpensesOrImpact && <>
        <DashboardProgressChartArea className={marginStyles.DefaultPanelMargins} title={`${titleMetric} door de tijd heen`} type="bar" spendingData={allSpendingData} showCo2={showCo2}
                                    chartScopeSettings={chartScopeSettings} setChartScopeSettings={setChartScopeSettings}
                                    selectedCategoryId={selectedCategoryId} setSelectedCategoryId={setSelectedCategoryId}
                                    setTransactionsCategoryId={setTransactionsCategoryId}/>

        <div className={marginStyles.DefaultSideMargins}>
          {spendingData && <>

            <div className={styles.Chart} data-hj-suppress={true}>
              <D3SpendingBarChart
                categoryId={selectedCategoryId ?? undefined}
                spendingData={spendingData}
                showCo2={showCo2}
                chartScopeSettings={chartScopeSettings}
                setChartScopeSettings={setChartScopeSettings}
                clickCategory={clickCategory}
                clickRemoveCategory={clickRemoveCategory}/>
            </div>

            <div>
              {message && <Message type="success">{message}</Message>}
              {showTransactions && hasTransactions && <>
                <Heading size="small">Transacties</Heading>
                <ChartScopeTransactions
                  chartScopeSettings={transactionsChartScopeSettings}
                  categoryId={transactionsCategoryId}
                  setCategoryId={setTransactionsCategoryId}
                  monthlySpendingData={spendingData.monthlySpendingData}
                  impactFirst={showCo2}
                  transactionRange={transactionRange}
                  refreshSpendingData={refreshTransactionData}
                  resetCategoryScope={resetCategoryScope}
                  doNotShowIncome/>
              </>}
            </div>
          </>}
        </div>
      </>}
    </>}

    {hasNoData && <>
      <Message type="alert" className={marginStyles.AllSidesMargins}>Je hebt nog geen transacties ingevoerd. Klik op het upload-icoon rechtsboven om transacties te importeren van je bank.</Message>
    </>}

    <ImportModal show={showImportModal} closeModal={() => setShowImportModal(false)} onSuccess={onImportSuccess} />
  </>
}